import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Menu, X, Check, Minus, ChevronDown, ChevronUp,
  ShieldCheck, RefreshCw, Clock, HeartHandshake,
  Linkedin, Facebook, MailIcon,
} from "lucide-react";

import { PLAN_PRICES, getPrice, getAnnualSavings, type Plan, type Cycle } from "@/lib/pricing";

type Segment = "homecare" | "grouphome";

// ---------- Nav ----------
function PricingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = "text-sm font-medium text-white/80 hover:text-[#D4A843] transition";

  return (
    <nav
      className="sticky top-0 z-50 transition-shadow"
      style={{
        backgroundColor: "#0F1E35",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.18)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/landing" className="text-white font-bold text-lg">
          <span style={{ color: "#D4A843" }}>LegacyGrowth</span> Engine
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <Link to="/landing" className={linkCls}>Home</Link>
          <Link to="/pricing" className={linkCls} style={{ color: "#D4A843" }}>Pricing</Link>
          <Link to="/affiliate" className={linkCls}>Affiliate</Link>
          <Link to="/login" className={linkCls}>Sign In</Link>
          <a
            href="#plans"
            className="px-5 py-2 rounded-lg font-semibold text-sm transition hover:brightness-110"
            style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
          >
            Start Free Trial
          </a>
        </div>
        <button
          className="md:hidden text-white"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden border-t border-white/10 px-6 py-4 space-y-3" style={{ backgroundColor: "#0F1E35" }}>
          <Link to="/landing" className="block text-white/80">Home</Link>
          <Link to="/pricing" className="block" style={{ color: "#D4A843" }}>Pricing</Link>
          <Link to="/affiliate" className="block text-white/80">Affiliate</Link>
          <Link to="/login" className="block text-white/80">Sign In</Link>
          <a href="#plans" className="block w-full text-center px-5 py-2 rounded-lg font-semibold" style={{ backgroundColor: "#D4A843", color: "#1F3864" }}>
            Start Free Trial
          </a>
        </div>
      )}
    </nav>
  );
}

// ---------- Footer ----------
function PricingFooter() {
  return (
    <footer style={{ backgroundColor: "#0A1628" }} className="text-white/70 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div>
          <div className="text-white font-bold text-lg mb-3">
            <span style={{ color: "#D4A843" }}>LegacyGrowth</span> Engine
          </div>
          <p className="text-sm leading-relaxed">
            Lead capture, scoring, and referral outreach for home care agencies and group homes.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Product</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/landing" className="hover:text-[#D4A843]">Features</Link></li>
            <li><Link to="/pricing" className="hover:text-[#D4A843]">Pricing</Link></li>
            <li><Link to="/login" className="hover:text-[#D4A843]">Sign In</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-[#D4A843]">About</a></li>
            <li><a href="#" className="hover:text-[#D4A843]">Contact</a></li>
            <li><Link to="/affiliate" className="hover:text-[#D4A843]">Affiliate Program</Link></li>
            <li><Link to="/api-docs" className="hover:text-[#D4A843]">API Docs</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3 text-sm">Connect</h4>
          <div className="flex gap-3">
            <a href="#" aria-label="LinkedIn" className="hover:text-[#D4A843]"><Linkedin size={18} /></a>
            <a href="#" aria-label="Facebook" className="hover:text-[#D4A843]"><Facebook size={18} /></a>
            <a href="mailto:hello@legacygrowthengine.com" aria-label="Email" className="hover:text-[#D4A843]"><MailIcon size={18} /></a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-white/10 text-xs text-white/50 flex flex-col md:flex-row gap-2 md:justify-between">
        <span>© {new Date().getFullYear()} H&amp;E Associates LLC. All rights reserved.</span>
        <span>HIPAA-Ready · BAA Available</span>
      </div>
    </footer>
  );
}

// ---------- Feature cell ----------
function FeatureCell({ value }: { value: boolean | string }) {
  if (value === true) {
    return (
      <td className="text-center px-4 py-3">
        <Check size={20} style={{ color: "#D4A843" }} className="inline" />
      </td>
    );
  }
  if (value === false) {
    return (
      <td className="text-center px-4 py-3">
        <Minus size={20} className="inline text-gray-300" />
      </td>
    );
  }
  return (
    <td className="text-center px-4 py-3 text-sm font-bold" style={{ color: "#1F3864" }}>
      {value}
    </td>
  );
}

// ---------- Page ----------
export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<Cycle>("monthly");
  const [segment, setSegment] = useState<Segment>("homecare");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [comparisonExpanded, setComparisonExpanded] = useState(false);

  // Inter font
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const seg = params.get("segment");
    const bill = params.get("billing");
    if (seg === "grouphome" || seg === "homecare") setSegment(seg);
    if (bill === "annual" || bill === "monthly") setBillingCycle(bill);
  }, []);

  const segLabel = segment === "homecare" ? "leads" : "inquiries";

  const starterFeatures = segment === "homecare" ? [
    "AI Lead Capture (Sarah chatbot)",
    "Lead Scoring Engine (0–100)",
    "Up to 50 leads per month",
    "5 Personalized Email Templates",
    "Email Notifications",
    "Golden ZIP Code Configuration",
    "Basic Dashboard & Lead Table",
    "Standard Support (email)",
    "14-day free trial",
  ] : [
    "AI Inquiry Capture",
    "Placement Scoring Engine (0–100)",
    "Up to 50 inquiries per month",
    "5 Personalized Email Templates",
    "Email Notifications",
    "Primary Locality Configuration",
    "Basic Dashboard & Inquiry Table",
    "Standard Support (email)",
    "14-day free trial",
  ];

  const growthFeatures = segment === "homecare" ? [
    "Everything in Starter",
    "Unlimited Leads",
    "All 14 Personalized Email Templates",
    "Outbound Referral Generator",
    "Automated Lead Enrichment",
    "Hot Lead SMS Alerts (score 85+)",
    "Emergency Lead SMS Alerts (score 100)",
    "Staff Role Access (up to 7 staff)",
    "Manager Role (up to 2 managers)",
    "Staff Utilization Report",
    "Weekly Owner Digest Email",
    "Priority Support",
  ] : [
    "Everything in Starter",
    "Unlimited Inquiries",
    "All 10 Personalized Email Templates",
    "Support Coordinator Outreach Generator",
    "Automated Inquiry Enrichment",
    "Urgent Inquiry SMS Alerts (score 85+)",
    "Emergency Placement SMS Alerts (score 100)",
    "Staff Role Access (up to 7 staff)",
    "Manager Role (up to 2 managers)",
    "Staff Utilization Report",
    "Weekly Owner Digest Email",
    "Priority Support",
  ];

  const proFeatures = segment === "homecare" ? [
    "Everything in Growth",
    "Multi-user Access (up to 10 staff, 3 managers)",
    "White-label AI Chatbot",
    "Custom Chatbot Persona Name & Color",
    "CRM Integration (coming soon)",
    "Voice AI Lead Capture (coming soon)",
    "Dedicated Onboarding Call",
    "SLA-backed Support Response",
    "Early Access to New Features",
    "Read-Only Viewer Seats (unlimited)",
    "Affiliate Program Access",
  ] : [
    "Everything in Growth",
    "Multi-user Access (up to 10 staff, 3 managers)",
    "White-label AI Intake Widget",
    "Custom Widget Persona Name & Color",
    "CRM Integration (coming soon)",
    "Voice AI Intake Capture (coming soon)",
    "Dedicated Onboarding Call",
    "SLA-backed Support Response",
    "Early Access to New Features",
    "Read-Only Viewer Seats (unlimited)",
    "Affiliate Program Access",
  ];

  type Row = { label: string; values: [boolean | string, boolean | string, boolean | string] };
  type Category = { name: string; rows: Row[] };

  const categories: Category[] = [
    {
      name: "LEAD CAPTURE & INTAKE",
      rows: [
        { label: "AI Chatbot / Intake Widget", values: [true, true, true] },
        { label: "Chatbot Persona", values: ["Sarah (default)", "Sarah (default)", "Custom name & color"] },
        { label: "Voice AI Capture", values: [false, false, "Coming soon"] },
        { label: `Monthly ${segment === "homecare" ? "Lead" : "Inquiry"} Limit`, values: ["50/month", "Unlimited", "Unlimited"] },
        { label: "Lead Transcript Storage", values: [true, true, true] },
      ],
    },
    {
      name: "LEAD SCORING & ENRICHMENT",
      rows: [
        { label: "Scoring Engine (0–100)", values: [true, true, true] },
        { label: segment === "homecare" ? "Golden ZIP Configuration" : "Primary Locality Config", values: [true, true, true] },
        { label: "Priority Specialty Configuration", values: [true, true, true] },
        { label: "Automated Lead Enrichment", values: [false, true, true] },
        { label: "Enrichment Score Boost", values: [false, "Up to +10 pts", "Up to +10 pts"] },
      ],
    },
    {
      name: "ALERTS & NOTIFICATIONS",
      rows: [
        { label: "Email Notifications", values: [true, true, true] },
        { label: "Hot Lead SMS Alert (85+)", values: [false, true, true] },
        { label: "Emergency Lead SMS Alert (100)", values: [false, true, true] },
        { label: "Weekly Owner Digest Email", values: [false, true, true] },
      ],
    },
    {
      name: "REFERRAL OUTREACH",
      rows: [
        { label: "Email Templates", values: ["5 templates", segment === "homecare" ? "14 templates" : "10 templates", segment === "homecare" ? "14 templates" : "10 templates"] },
        { label: "Outbound Referral Generator", values: [false, true, true] },
        { label: "Referral Lead Enrichment", values: [false, true, true] },
        { label: "Email Scheduling", values: [true, true, true] },
        { label: "Send via Resend Integration", values: [true, true, true] },
      ],
    },
    {
      name: "TEAM & ROLES",
      rows: [
        { label: "Owner Seats", values: ["1", "1", "1"] },
        { label: "Manager Seats", values: ["1", "2", "3"] },
        { label: "Staff Seats", values: ["3", "7", "10"] },
        { label: "Read-Only Viewer Seats", values: [false, false, "Unlimited"] },
        { label: "Staff Task View (mobile)", values: [true, true, true] },
        { label: "Staff Utilization Report", values: [false, true, true] },
        { label: "Lead Assignment & Tracking", values: [true, true, true] },
      ],
    },
    {
      name: "ANALYTICS & REPORTING",
      rows: [
        { label: "Dashboard KPI Cards", values: ["4 cards", "6 cards", "6 cards"] },
        { label: "Pipeline & Revenue Analytics", values: [false, true, true] },
        { label: "Referral Source Performance Report", values: [false, true, true] },
        { label: "Lead Outcomes Analysis", values: [false, true, true] },
        { label: "Staff Activity Report", values: [false, true, true] },
        { label: "Data Export (CSV)", values: [false, true, true] },
      ],
    },
    {
      name: "INTEGRATIONS",
      rows: [
        { label: "Calendly / TidyCal Booking Link", values: [true, true, true] },
        { label: "Supabase Database", values: [true, true, true] },
        { label: "Twilio SMS", values: [false, true, true] },
        { label: "Resend Email", values: [true, true, true] },
        { label: "CRM Integration", values: [false, false, "Coming soon"] },
      ],
    },
    {
      name: "COMPLIANCE & SECURITY",
      rows: [
        { label: "HIPAA-Ready Infrastructure", values: [true, true, true] },
        { label: "Business Associate Agreement (BAA)", values: [true, true, true] },
        { label: "Data Encryption (at rest & in transit)", values: [true, true, true] },
        { label: "DBHDS Referral Tracking Support", values: [true, true, true] },
      ],
    },
    {
      name: "SUPPORT",
      rows: [
        { label: "Email Support", values: [true, true, true] },
        { label: "Priority Support", values: [false, true, true] },
        { label: "Dedicated Onboarding Call", values: [false, false, true] },
        { label: "SLA-backed Response Time", values: [false, false, "< 4 business hours"] },
        { label: "Early Access to New Features", values: [false, false, true] },
      ],
    },
    {
      name: "PROGRAM",
      rows: [
        { label: "14-Day Free Trial", values: [true, true, true] },
        { label: "No Credit Card Required (trial)", values: [true, true, true] },
        { label: "Cancel Anytime", values: [true, true, true] },
        { label: "Affiliate Program Access", values: [false, false, true] },
      ],
    },
  ];

  // Flatten with category headers, then count first 8 rows
  type FlatItem = { type: "header"; name: string } | { type: "row"; row: Row };
  const flat: FlatItem[] = [];
  for (const cat of categories) {
    flat.push({ type: "header", name: cat.name });
    for (const r of cat.rows) flat.push({ type: "row", row: r });
  }
  // Show first 8 ROWS (not items) when collapsed
  let rowsShown = 0;
  const collapsedFlat: FlatItem[] = [];
  for (const item of flat) {
    if (item.type === "row" && rowsShown >= 8) break;
    collapsedFlat.push(item);
    if (item.type === "row") rowsShown++;
  }
  const visibleFlat = comparisonExpanded ? flat : collapsedFlat;

  const faqs = [
    { q: "What happens when my free trial ends?", a: "When your 14-day free trial ends, your account moves to a read-only state. Your data is preserved for 30 days. You can upgrade at any time within that window and everything — your leads, your filters, your team, your templates — comes back instantly. After 30 days of inactivity, data may be purged." },
    { q: "Can I change plans after I sign up?", a: "Yes. You can upgrade or downgrade at any time from your account settings. Upgrades take effect immediately. Downgrades take effect at the start of your next billing cycle. If you downgrade and exceed the lower plan's seat or lead limits, you will be prompted to upgrade again." },
    { q: "Is there a discount for annual billing?", a: "Yes — annual billing saves you 20% compared to monthly. Starter drops from $97 to $78/month, Growth from $197 to $158/month, and Agency Pro from $347 to $278/month. Annual plans are billed in full at the start of the billing period." },
    { q: "Does the free trial require a credit card?", a: "No. You can start your 14-day free trial without entering any payment information. You will only be asked for a card when you choose to upgrade to a paid plan." },
    { q: `What counts as a ${segLabel.slice(0, -1)} toward the Starter plan limit?`, a: `Any ${segLabel.slice(0, -1)} captured through your AI chatbot or added manually through the dashboard counts toward your monthly limit. The limit resets on the 1st of every month. If you reach 50 ${segLabel} mid-month, your chatbot will pause until the next reset or until you upgrade.` },
    { q: "Can I add more team members than my plan allows?", a: "No — seat limits are enforced by the platform. If you need more Manager or Staff seats, you will need to upgrade your plan. See the plan comparison table above for seat limits per plan." },
    { q: "Does the platform work for both home care and group home operators?", a: "Yes. LegacyGrowth Engine is built for both audiences. During onboarding you select your organization type and the platform personalizes your scoring engine, email templates, dashboard labels, and referral source lists accordingly. You can update your organization type in Settings at any time." },
    { q: "How do I access the affiliate program?", a: "The affiliate program is available to Agency Pro subscribers. After upgrading, you will find your affiliate dashboard and unique referral link in the Settings tab under Affiliate Program. See our Referral Partner Program page for full details on commission structure and payout terms." },
  ];

  const scrollToCompare = () => {
    document.getElementById("compare")?.scrollIntoView({ behavior: "smooth" });
  };

  const planCard = (plan: Plan, isCenter: boolean, name: string, tagline: string, features: string[], ctaPath: string) => {
    const price = getPrice(plan, billingCycle);
    const savings = getAnnualSavings(plan);
    const bg = isCenter ? "#1F3864" : "#FFFFFF";
    const titleColor = isCenter ? "#FFFFFF" : "#1F3864";
    const taglineColor = isCenter ? "#9AAFC8" : "#6B7280";
    const priceColor = isCenter ? "#FFFFFF" : "#1F3864";
    const featureColor = isCenter ? "#E5E7EB" : "#374151";
    const dividerColor = isCenter ? "rgba(255,255,255,0.15)" : "#E5E7EB";

    return (
      <div
        className={`relative flex h-full flex-col rounded-[14px] p-8 transition-transform duration-300 hover:-translate-y-1 ${isCenter ? "md:-translate-y-3 hover:md:-translate-y-4" : ""}`}
        style={{
          backgroundColor: bg,
          boxShadow: isCenter ? "0 12px 40px rgba(15,30,53,0.25)" : "0 4px 24px rgba(0,0,0,0.08)",
          border: isCenter ? "2px solid #D4A843" : "1px solid #E5E7EB",
        }}
      >
        {isCenter && (
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-md text-xs font-bold tracking-wider"
            style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
          >
            MOST POPULAR
          </div>
        )}
        <h3 className="text-[22px] font-bold" style={{ color: titleColor }}>{name}</h3>
        <p className="text-sm mt-1" style={{ color: taglineColor }}>{tagline}</p>
        <div className="mt-5">
          <span className="text-[48px] font-bold leading-none" style={{ color: priceColor }}>${price}</span>
          <span className="text-sm ml-1" style={{ color: taglineColor }}>/mo</span>
          {billingCycle === "annual" && (
            <p className="text-xs mt-1" style={{ color: taglineColor }}>billed annually</p>
          )}
          {billingCycle === "annual" && (
            <div className="mt-3 inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "rgba(212,168,67,0.15)", color: "#D4A843", border: "1px solid #D4A843" }}>
              Save ${savings}/yr
            </div>
          )}
        </div>
        <div className="my-5 h-px w-full" style={{ backgroundColor: dividerColor }} />
        <ul className="flex-1 space-y-2.5">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: featureColor }}>
              <Check size={16} style={{ color: "#D4A843" }} className="mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <Link
          to={ctaPath}
          className={`mt-6 block w-full text-center py-3 rounded-lg font-semibold transition hover:brightness-110 ${isCenter ? "" : "border-2"}`}
          style={
            isCenter
              ? { backgroundColor: "#D4A843", color: "#1F3864" }
              : { borderColor: "#1F3864", color: "#1F3864" }
          }
        >
          Start Free Trial
        </Link>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "Inter, sans-serif", backgroundColor: "#F8F9FB" }} className="min-h-screen">
      <PricingNav />

      {/* HERO */}
      <section style={{ backgroundColor: "#0F1E35" }} className="px-6 lg:px-10 pt-20 pb-16 text-center">
        <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>
          SIMPLE, TRANSPARENT PRICING
        </p>
        <h1 className="text-white font-bold mt-4" style={{ fontSize: "clamp(32px,5vw,52px)", lineHeight: 1.15 }}>
          No Surprises. No Hidden Fees.<br />Just Growth.
        </h1>
        <p className="text-white/70 mt-5 max-w-[560px] mx-auto text-[17px] leading-relaxed">
          Every plan includes a 14-day free trial. No credit card required. Cancel anytime. Pick the plan that fits where your agency is today — upgrade as you grow.
        </p>

        {/* Segment toggle */}
        <div className="mt-10 inline-flex p-1 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
          {(["homecare", "grouphome"] as Segment[]).map((s) => (
            <button
              key={s}
              onClick={() => setSegment(s)}
              className="px-5 py-2.5 rounded-full text-sm font-semibold transition"
              style={{
                backgroundColor: segment === s ? "#D4A843" : "transparent",
                color: segment === s ? "#1F3864" : "rgba(255,255,255,0.7)",
              }}
            >
              {s === "homecare" ? "Home Care Agency" : "Group Home / Residential"}
            </button>
          ))}
        </div>

        {/* Billing cycle */}
        <div className="mt-5 inline-flex items-center p-1 rounded-full ml-0 md:ml-3" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
          <button
            onClick={() => setBillingCycle("monthly")}
            className="px-5 py-2 rounded-full text-sm font-semibold transition"
            style={{
              backgroundColor: billingCycle === "monthly" ? "#D4A843" : "transparent",
              color: billingCycle === "monthly" ? "#1F3864" : "rgba(255,255,255,0.7)",
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className="px-5 py-2 rounded-full text-sm font-semibold transition flex items-center gap-2"
            style={{
              backgroundColor: billingCycle === "annual" ? "#D4A843" : "transparent",
              color: billingCycle === "annual" ? "#1F3864" : "rgba(255,255,255,0.7)",
            }}
          >
            Annual
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: billingCycle === "annual" ? "#1F3864" : "#D4A843",
                color: billingCycle === "annual" ? "#D4A843" : "#1F3864",
              }}
            >
              Save 20%
            </span>
          </button>
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" style={{ backgroundColor: "#F8F9FB" }} className="px-6 lg:px-10 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {planCard(
            "starter",
            false,
            "Starter",
            segment === "homecare" ? "For agencies just getting started" : "For programs with 1–2 residential homes",
            starterFeatures,
            "/signup?plan=starter"
          )}
          {planCard(
            "growth",
            true,
            "Growth",
            segment === "homecare" ? "For agencies ready to scale" : "For programs managing 3–5 homes",
            growthFeatures,
            "/signup?plan=growth"
          )}
          {planCard(
            "agency-pro",
            false,
            "Agency Pro",
            segment === "homecare" ? "For multi-location or high-volume agencies" : "For multi-site operators and growing organizations",
            proFeatures,
            "/signup?plan=agency-pro"
          )}
        </div>
        <p className="text-center text-sm mt-8 text-gray-500 max-w-2xl mx-auto">
          All plans include a 14-day free trial. No credit card required. Cancel anytime. Annual plans are billed in full at the start of the billing period.
        </p>
      </section>

      {/* COMPARISON TABLE */}
      <section id="compare" className="bg-white px-6 lg:px-10 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[2px]" style={{ color: "#D4A843" }}>COMPARE PLANS</p>
          <h2 className="font-bold mt-3" style={{ color: "#1F3864", fontSize: "clamp(28px,4vw,40px)" }}>
            Everything You Need to Know Before You Decide
          </h2>
          <button
            onClick={() => setComparisonExpanded(!comparisonExpanded)}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "#D4A843" }}
          >
            {comparisonExpanded ? "Hide Comparison" : "Show Full Comparison"}
            {comparisonExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>

        <div
          className="max-w-5xl mx-auto mt-8 overflow-hidden transition-all duration-[400ms] ease-in-out"
          style={{ maxHeight: comparisonExpanded ? "8000px" : "900px" }}
        >
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ backgroundColor: "#0F1E35" }}>
                  <th className="text-left text-white text-sm font-bold px-5 py-4 w-[40%]">Feature</th>
                  <th className="text-center text-white text-base font-bold px-4 py-4">Starter</th>
                  <th className="text-center text-white text-base font-bold px-4 py-4">
                    Growth
                    <div className="text-[11px] font-semibold mt-0.5" style={{ color: "#D4A843" }}>⭐ Most Popular</div>
                  </th>
                  <th className="text-center text-white text-base font-bold px-4 py-4">Agency Pro</th>
                </tr>
              </thead>
              <tbody>
                {visibleFlat.map((item, idx) => {
                  if (item.type === "header") {
                    return (
                      <tr key={`h-${idx}`} style={{ backgroundColor: "#1F3864" }}>
                        <td colSpan={4} className="px-5 py-2.5 text-white text-[13px] font-bold tracking-wider">
                          {item.name}
                        </td>
                      </tr>
                    );
                  }
                  const altBg = idx % 2 === 0 ? "#FFFFFF" : "#F8F9FB";
                  return (
                    <tr key={`r-${idx}`} style={{ backgroundColor: altBg }}>
                      <td className="px-5 py-3 text-sm font-medium" style={{ color: "#1F3864" }}>
                        {item.row.label}
                      </td>
                      <FeatureCell value={item.row.values[0]} />
                      <FeatureCell value={item.row.values[1]} />
                      <FeatureCell value={item.row.values[2]} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA row under table */}
        <div className="max-w-5xl mx-auto mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="hidden md:block" />
          <Link to="/signup?plan=starter" className="text-center py-3 rounded-lg font-semibold border-2 transition hover:bg-gray-50" style={{ borderColor: "#1F3864", color: "#1F3864" }}>
            Start Starter Trial
          </Link>
          <Link to="/signup?plan=growth" className="text-center py-3 rounded-lg font-semibold transition hover:brightness-110" style={{ backgroundColor: "#D4A843", color: "#1F3864" }}>
            Start Growth Trial
          </Link>
          <Link to="/signup?plan=agency-pro" className="text-center py-3 rounded-lg font-semibold border-2 transition hover:bg-gray-50" style={{ borderColor: "#1F3864", color: "#1F3864" }}>
            Start Pro Trial
          </Link>
        </div>
      </section>

      {/* ENTERPRISE */}
      <section style={{ backgroundColor: "#0F1E35" }} className="px-6 lg:px-10 py-16 text-center">
        <h2 className="text-white font-bold" style={{ fontSize: "clamp(26px,3.5vw,36px)" }}>
          Running more than 5 locations or homes?
        </h2>
        <p className="text-white/70 mt-4 max-w-[560px] mx-auto text-[17px] leading-relaxed">
          LegacyGrowth Engine offers custom enterprise pricing for multi-site operators, management companies, and franchise networks. Volume discounts, dedicated implementation support, and custom integrations available.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="mailto:enterprise@legacygrowthengine.com"
            className="px-6 py-3 rounded-lg font-semibold transition hover:brightness-110"
            style={{ backgroundColor: "#D4A843", color: "#1F3864" }}
          >
            Talk to Our Team
          </a>
          <a
            href="#"
            className="px-6 py-3 rounded-lg font-semibold border-2 border-white text-white transition hover:bg-white/10"
          >
            Download Enterprise Overview
          </a>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="bg-white px-6 lg:px-10 py-12">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { Icon: ShieldCheck, title: "HIPAA-Ready", desc: "BAA included with every plan. Your client data is protected." },
            { Icon: RefreshCw, title: "Cancel Anytime", desc: "No contracts. No penalties. Cancel from your account settings in two clicks." },
            { Icon: Clock, title: "Setup in 30 Minutes", desc: "Onboarding takes under 30 minutes. Your chatbot can be live today." },
            { Icon: HeartHandshake, title: "Built for Your World", desc: "Designed specifically for home care and residential program operators — not generic SaaS." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="text-center">
              <Icon size={32} style={{ color: "#D4A843" }} className="mx-auto" />
              <h3 className="font-bold mt-3 text-[15px]" style={{ color: "#1F3864" }}>{title}</h3>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white px-6 lg:px-10 pt-12 pb-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-semibold tracking-[2px] text-center" style={{ color: "#D4A843" }}>PRICING FAQ</p>
          <h2 className="font-bold text-center mt-3" style={{ color: "#1F3864", fontSize: "clamp(26px,3.5vw,36px)" }}>
            Your Pricing Questions, Answered
          </h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-[15px]" style={{ color: "#1F3864" }}>{f.q}</span>
                  <ChevronDown
                    size={20}
                    className="transition-transform shrink-0"
                    style={{ color: "#D4A843", transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm leading-relaxed text-gray-600">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{ backgroundColor: "#0F1E35", backgroundImage: "radial-gradient(circle at 50% 0%, rgba(212,168,67,0.15), transparent 60%)" }} className="px-6 lg:px-10 py-20 text-center">
        <h2 className="text-white font-bold" style={{ fontSize: "clamp(32px,5vw,48px)" }}>
          Start Free. Upgrade When You're Ready.
        </h2>
        <p className="text-white/70 mt-4 text-[17px]">14 days. No credit card. Everything unlocked.</p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup" className="px-7 py-3.5 rounded-lg font-bold transition hover:brightness-110" style={{ backgroundColor: "#D4A843", color: "#1F3864" }}>
            Start My Free Trial →
          </Link>
          <button onClick={scrollToCompare} className="px-7 py-3.5 rounded-lg font-bold border-2 border-white text-white transition hover:bg-white/10">
            Compare All Features ↑
          </button>
        </div>
        <p className="text-white/50 text-sm mt-6">No credit card required  ·  HIPAA-Ready  ·  Cancel anytime</p>
      </section>

      <PricingFooter />
    </div>
  );
}
